import {
  PropertyFilters,
  SortOption,
} from "@/components/find/PropertyFilters";

/**
 * Serializes the Find page's committed search into URL query params and back.
 *
 * The URL is the single source of truth for "what was actually searched", which
 * lets refresh / back-forward / shared links restore a search without the page
 * having to fire an extra request to rebuild state.
 */

const SEARCH_KEY = "q";
const TYPES_KEY = "types";
const SORT_KEY = "sort";
const DEMO_KEY = "demo";

/** Where the page searches when the user hasn't picked a location yet. */
export const DEFAULT_SEARCH_LOCATION = "Birmingham, AL";

const NUMERIC_KEYS = [
  "priceMin",
  "priceMax",
  "bedsMin",
  "bathsMin",
  "sqftMin",
  "sqftMax",
  "capRateMin",
  "cashOnCashMin",
  "grmMax",
  "daysOnMarketMax",
] as const;

const BOOLEAN_KEYS = [
  "foreclosure",
  "preForeclosure",
  "auction",
  "reo",
  "priceReduced",
  "belowMarketValue",
  "motivatedSeller",
] as const;

const SORT_OPTIONS: readonly SortOption[] = [
  "newest",
  "price_low",
  "price_high",
  "cap_rate",
  "coc",
  "days_high",
  "days_low",
];

const DEFAULT_SORT: SortOption = "newest";

interface ParsedSearch {
  /** `null` only in demo mode (`?demo=1`). Otherwise always a real search. */
  filters: PropertyFilters | null;
  sortBy: SortOption;
}

/** Fresh blank filters. A factory, not a shared constant, so callers can't mutate it. */
export function createEmptyFilters(): PropertyFilters {
  return {
    search: "",
    propertyTypes: [],
    foreclosure: false,
    preForeclosure: false,
    auction: false,
    reo: false,
    priceReduced: false,
    belowMarketValue: false,
    motivatedSeller: false,
  };
}

export function toSearchParams(
  filters: PropertyFilters,
  sortBy: SortOption = DEFAULT_SORT,
): URLSearchParams {
  const params = new URLSearchParams();

  const search = filters.search.trim();
  if (search) {
    params.set(SEARCH_KEY, search);
  }

  for (const key of NUMERIC_KEYS) {
    const value = filters[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      params.set(key, String(value));
    }
  }

  for (const key of BOOLEAN_KEYS) {
    if (filters[key]) {
      params.set(key, "1");
    }
  }

  // Sorted so two searches that differ only in checkbox click order produce the
  // same URL — and therefore the same TanStack Query cache key.
  const propertyTypes = [...filters.propertyTypes].sort();
  if (propertyTypes.length > 0) {
    params.set(TYPES_KEY, propertyTypes.join(","));
  }

  if (sortBy !== DEFAULT_SORT) {
    params.set(SORT_KEY, sortBy);
  }

  return params;
}

/** Blank filters pointed at the default location — what a cold page load searches. */
export function createDefaultFilters(): PropertyFilters {
  return { ...createEmptyFilters(), search: DEFAULT_SEARCH_LOCATION };
}

/** Params for demo mode. Demo is an explicit URL state, not "no search". */
export function toDemoSearchParams(
  sortBy: SortOption = DEFAULT_SORT,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set(DEMO_KEY, "1");
  if (sortBy !== DEFAULT_SORT) {
    params.set(SORT_KEY, sortBy);
  }
  return params;
}

export function fromSearchParams(params: URLSearchParams): ParsedSearch {
  const sortParam = params.get(SORT_KEY);
  const sortBy = SORT_OPTIONS.find((option) => option === sortParam) ?? DEFAULT_SORT;

  if (params.get(DEMO_KEY) === "1") {
    return { filters: null, sortBy };
  }

  const filters = createEmptyFilters();
  filters.search = params.get(SEARCH_KEY)?.trim() || DEFAULT_SEARCH_LOCATION;

  for (const key of NUMERIC_KEYS) {
    const raw = params.get(key);
    if (!raw) continue;
    const value = Number(raw);
    if (Number.isFinite(value)) {
      filters[key] = value;
    }
  }

  for (const key of BOOLEAN_KEYS) {
    filters[key] = params.get(key) === "1";
  }

  const types = params.get(TYPES_KEY);
  filters.propertyTypes = types
    ? types.split(",").filter(Boolean).sort()
    : [];

  return { filters, sortBy };
}

/**
 * Where the last committed search is remembered between visits to the page.
 *
 * sessionStorage, not localStorage: leaving the Find page and coming back should
 * resume where you were, but opening the app fresh should start at the default
 * location rather than a search from days ago.
 */
const STORAGE_KEY = "find:last-search";

/** The last committed search this session, or "" if there isn't one. */
export function readStoredSearch(): string {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    // Storage can throw in private mode or when cookies are blocked. Resuming a
    // search is a convenience, so degrade to the default rather than breaking.
    return "";
  }
}

export function writeStoredSearch(params: string): void {
  try {
    if (params) {
      sessionStorage.setItem(STORAGE_KEY, params);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // See readStoredSearch.
  }
}

/**
 * Compares two filter sets by their serialized form, ignoring sort. Used to tell
 * the user their draft filters differ from what is currently on screen.
 */
export function areFiltersEqual(
  a: PropertyFilters | null,
  b: PropertyFilters | null,
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return toSearchParams(a).toString() === toSearchParams(b).toString();
}
